import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { AvatarCrowd } from './AvatarCrowd';
import { createAvatar, createNewYorkScene, batchStaticScene, createCommunityGarden } from './nyc-kit';
import { locations, npcs, text, type Locale } from '../../shared/content';
import type { Character, ScenePeer } from '../../shared/types';
import {
  MapPin,
  Home,
  Coffee,
  BriefcaseBusiness,
  Landmark,
  Trees,
  Store,
  Palette,
  Pizza,
  Sparkles,
  Plus,
  Minus,
} from 'lucide-react';
import { samplePath } from '../../shared/navigation';
import { SceneBillboard, sponsorLabel } from '../ads/billboard';
import { billboardVisibility, elementIsPainted } from '../ads/visibility';
import { safeClickPath, type AdDelivery } from '../ads/useAdDelivery';
import { translate } from '../i18n';
import {
  activeEmote,
  emoteCopy,
  EMOTE_PRESETS,
  type EmotePreset,
  type SceneEmote,
} from '../../shared/emotes';
const icons: Record<string, typeof Home> = {
  home: Home,
  cafe: Coffee,
  bodega: Store,
  jobs: BriefcaseBusiness,
  'city-hall': Landmark,
  park: Trees,
  bryant: Trees,
  studio: Palette,
  pizza: Pizza,
  laundry: Sparkles,
};
export default function CityScene({
  character,
  locale,
  onPlace,
  selected,
  offset = 0,
  peers = [],
  gardenStage = 0,
  emotes = {},
  onEmote,
  emotesEnabled = false,
  billboards = { subway: null, cafe: null },
}: {
  peers?: ScenePeer[];
  character: Character | null;
  locale: Locale;
  onPlace: (id: string) => void;
  selected: string;
  offset?: number;
  gardenStage?: number;
  emotes?: Record<string, SceneEmote>;
  onEmote?: (preset: EmotePreset) => void;
  emotesEnabled?: boolean;
  billboards?: { subway: AdDelivery | null; cafe: AdDelivery | null };
}) {
  const host = useRef<HTMLDivElement>(null);
  const state = useRef({
    character,
    onPlace,
    selected,
    offset,
    peers,
    gardenStage,
    locale,
    emotes,
    billboards,
  });
  state.current = { character, onPlace, selected, offset, peers, gardenStage, locale, emotes, billboards };
  const npcLabels = useRef(new Map<string, HTMLDivElement>());
  const labels = useRef(new Map<string, HTMLButtonElement>());
  const avatarLabel = useRef<HTMLDivElement>(null);
  const ownEmoteLabel = useRef<HTMLElement>(null);
  const controls = useRef<(delta: number) => void>(() => {});
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    const el = host.current!;
    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'low-power' });
    } catch {
      setFailed(true);
      return;
    }
    renderer.setPixelRatio(Math.min(devicePixelRatio, 1.5));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.35;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFShadowMap;
    el.prepend(renderer.domElement);
    const scene = new THREE.Scene();
    scene.background = new THREE.Color('#dfded0');
    scene.fog = new THREE.Fog('#dfded0', 65, 125);
    const camera = new THREE.OrthographicCamera(-30, 30, 25, -25, 0.1, 200);
    camera.position.set(34, 33, 43);
    camera.lookAt(0, 1.5, -5);
    const cameraFocus = new THREE.Vector3(0, 1.5, -5),
      cameraOffset = new THREE.Vector3(34, 31.5, 48),
      desiredFocus = new THREE.Vector3();
    scene.add(new THREE.HemisphereLight('#fff5d8', '#9fa88c', 2.5));
    const sun = new THREE.DirectionalLight('#ffe9bc', 3.2);
    sun.position.set(-25, 45, 20);
    sun.castShadow = true;
    sun.shadow.mapSize.set(1024, 1024);
    sun.shadow.camera.left = -35;
    sun.shadow.camera.right = 35;
    sun.shadow.camera.top = 30;
    sun.shadow.camera.bottom = -30;
    sun.shadow.bias = -0.0008;
    sun.shadow.normalBias = 0.05;
    scene.add(sun);
    const city = batchStaticScene(createNewYorkScene());
    scene.add(city);
    const boards = (['subway', 'cafe'] as const).map((placement) => {
      const board = new SceneBillboard(placement);
      const label = document.createElement('div');
      Object.assign(label.style, {
        position: 'absolute',
        top: '0',
        left: '0',
        pointerEvents: 'none',
        font: 'bold 12px system-ui, sans-serif',
        color: '#fdf1d5',
        background: '#315f55',
        padding: '3px 6px',
        borderRadius: '3px',
        maxWidth: '210px',
        textAlign: 'center',
        zIndex: '1',
      });
      label.hidden = true;
      el.appendChild(label);
      scene.add(board.structure.group);
      return { placement, board, label, paintedToken: '', labelShiftX: 0, labelShiftY: 0 };
    });
    const adRay = new THREE.Raycaster();
    let measuredAt = 0;
    let shownGardenStage = -1,
      garden: THREE.Group | undefined;
    let avatar = createAvatar(character?.data.appearance);
    avatar.position.set(character?.data.x ?? -13.5, 0.05, character?.data.z ?? -3.5);
    const crowd = new AvatarCrowd();
    scene.add(crowd.group);
    const npcAvatars = npcs.map((n, i) => {
      const a = createAvatar({
        shirt: n.color,
        skin: ['#c58a63', '#7f513b', '#e1b899'][i % 3],
        hair: i % 2 ? '#453a31' : '#302c29',
      });
      const l = locations.find((l) => l.id === n.location)!;
      a.position.set(l.x + 1, 0.04, l.z + 0.9);
      a.scale.setScalar(0.82);
      a.rotation.y = 0.5 + i * 0.5;
      return a;
    });
    const ring = new THREE.Mesh(
      new THREE.RingGeometry(0.55, 0.68, 32),
      new THREE.MeshBasicMaterial({ color: '#fff8da', side: THREE.DoubleSide }),
    );
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.08;
    scene.add(ring);
    const peerObjects = new Map<string, { avatar: THREE.Group; label: HTMLDivElement; appearance: string }>();
    const clickRing = new THREE.Mesh(
      new THREE.RingGeometry(0.45, 0.5, 32),
      new THREE.MeshBasicMaterial({
        color: '#2d7166',
        transparent: true,
        opacity: 0.7,
        side: THREE.DoubleSide,
      }),
    );
    clickRing.rotation.x = -Math.PI / 2;
    clickRing.position.y = 0.06;
    scene.add(clickRing);
    let zoom = 1,
      w = 1,
      h = 1,
      last = 0,
      raf = 0;
    const projected = new THREE.Vector3();
    let currentAppearance = '';
    const resize = () => {
      w = el.clientWidth;
      h = el.clientHeight;
      renderer.setSize(w, h);
      const aspect = w / h;
      const span = w < 700 ? 40 : 41;
      camera.left = (-span * aspect) / 2 / zoom;
      camera.right = (span * aspect) / 2 / zoom;
      camera.top = span / 2 / zoom;
      camera.bottom = -span / 2 / zoom;
      camera.updateProjectionMatrix();
    };
    controls.current = (delta) => {
      zoom = THREE.MathUtils.clamp(zoom + delta, 0.7, 1.7);
      resize();
    };
    const observer = new ResizeObserver(resize);
    observer.observe(el);
    resize();
    const project = (x: number, y: number, z: number, node: HTMLElement | null) => {
      if (!node) return;
      projected.set(x, y, z).project(camera);
      node.style.transform = `translate(${(projected.x * 0.5 + 0.5) * w}px,${(-projected.y * 0.5 + 0.5) * h}px) translate(-50%,-50%)`;
      node.style.visibility = projected.z > 1 ? 'hidden' : 'visible';
    };
    let metricsAt = performance.now(),
      metricsFrames = 0;
    const draw = (stamp: number) => {
      raf = requestAnimationFrame(draw);
      if (document.hidden || stamp - last < 33) return;
      last = stamp;
      const { character: c, selected: s, offset } = state.current;
      const serverNow = Date.now() + offset;
      const ownEmote = c ? activeEmote(state.current.emotes[c.id], serverNow) : undefined;
      if (ownEmoteLabel.current) {
        ownEmoteLabel.current.hidden = !ownEmote;
        ownEmoteLabel.current.textContent = ownEmote ? emoteCopy[ownEmote].emoji : '';
      }
      const focusedLocation = locations.find((l) => l.id === s);
      if (w < 700 && c) {
        const destination = c.data.travel ? avatar.position : focusedLocation || { x: c.data.x, z: c.data.z };
        desiredFocus.set(destination.x, 1.5, destination.z);
      } else desiredFocus.set(0, 1.5, -5);
      cameraFocus.lerp(desiredFocus, 0.12);
      camera.position.copy(cameraFocus).add(cameraOffset);
      camera.lookAt(cameraFocus);
      camera.updateMatrixWorld();
      if (shownGardenStage !== state.current.gardenStage) {
        if (garden) {
          scene.remove(garden);
          garden.traverse((o) => {
            if (o instanceof THREE.InstancedMesh) o.dispose();
          });
        }
        shownGardenStage = state.current.gardenStage;
        garden = createCommunityGarden(shownGardenStage);
        scene.add(garden);
      }
      let moving = false;
      if (c) {
        const p = c.data;
        let x = p.x,
          z = p.z;
        if (p.travel) {
          const target = locations.find((l) => l.id === p.travel!.destination)!;
          const t = THREE.MathUtils.clamp(
            (Date.now() + offset - Date.parse(p.travel.startedAt)) /
              (Date.parse(p.travel.readyAt) - Date.parse(p.travel.startedAt)),
            0,
            1,
          );
          const point = samplePath(
            p.travel.path || [
              { x: p.travel.fromX, z: p.travel.fromZ },
              { x: target.x, z: target.z },
            ],
            t,
          );
          x = point.x;
          z = point.z;
          moving = t < 1;
          avatar.rotation.y = Math.atan2(target.x - p.travel.fromX, target.z - p.travel.fromZ);
        }
        avatar.position.set(x, 0.04, z);
        ring.position.set(x, 0.07, z);
        const a = JSON.stringify(p.appearance);
        if (a !== currentAppearance) {
          const previous = avatar;
          avatar = createAvatar(p.appearance);
          avatar.position.copy(previous.position);
          avatar.rotation.copy(previous.rotation);

          currentAppearance = a;
        }
        for (const [name, sign] of [
          ['leftLeg', 1],
          ['rightLeg', -1],
          ['leftArm', -1],
          ['rightArm', 1],
        ] as const) {
          const part = avatar.getObjectByName(name);
          if (part) part.rotation.x = moving ? Math.sin(stamp * 0.013) * 0.5 * sign : 0;
        }
        project(x, 2.8, z, avatarLabel.current);
      } else project(avatar.position.x, 2.8, avatar.position.z, avatarLabel.current);
      const ids = new Set(state.current.peers.map((p) => p.characterId));
      for (const [id, obj] of peerObjects)
        if (!ids.has(id)) {
          scene.remove(obj.avatar);
          obj.label.remove();
          peerObjects.delete(id);
        }
      for (const peer of state.current.peers) {
        let obj = peerObjects.get(peer.characterId);
        const appearance = JSON.stringify([peer.shirt, peer.skin, peer.hair, peer.trousers]);
        if (obj && obj.appearance !== appearance) {
          const old = obj.avatar;
          obj.avatar = createAvatar(peer);
          obj.avatar.position.copy(old.position);
          obj.avatar.rotation.copy(old.rotation);

          obj.appearance = appearance;
        }
        if (!obj) {
          const a = createAvatar(peer);
          const label = document.createElement('div');
          label.className = 'peer-label';
          label.textContent = peer.name;
          el.appendChild(label);
          obj = { avatar: a, label, appearance };
          peerObjects.set(peer.characterId, obj);
        }
        const expression = activeEmote(state.current.emotes[peer.characterId], serverNow);
        const labelText = expression ? `${emoteCopy[expression].emoji} ${peer.name}` : peer.name;
        if (obj.label.textContent !== labelText) obj.label.textContent = labelText;
        obj.label.dataset.emote = expression || '';
        obj.label.title = expression
          ? `${peer.name} · ${emoteCopy[expression].label[state.current.locale]}`
          : peer.name;
        obj.label.style.fontSize = expression ? '14px' : '';
        let x = peer.x,
          z = peer.z;
        let walking = false;
        try {
          const travel = JSON.parse(peer.travel);
          if (travel) {
            const dest = locations.find((l) => l.id === travel.destination)!;
            const frac =
              (Date.now() + state.current.offset - Date.parse(travel.startedAt)) /
              (Date.parse(travel.readyAt) - Date.parse(travel.startedAt));
            const pt = samplePath(
              travel.path || [
                { x: travel.fromX, z: travel.fromZ },
                { x: dest.x, z: dest.z },
              ],
              frac,
            );
            x = pt.x;
            z = pt.z;
            walking = frac >= 0 && frac < 1;
            const ahead = samplePath(
              travel.path || [
                { x: travel.fromX, z: travel.fromZ },
                { x: dest.x, z: dest.z },
              ],
              Math.min(1, frac + 0.025),
            );
            if (walking) obj.avatar.rotation.y = Math.atan2(ahead.x - x, ahead.z - z);
          }
        } catch {}
        obj.avatar.position.set(x, 0.04, z);
        for (const [name, sign] of [
          ['leftLeg', 1],
          ['rightLeg', -1],
          ['leftArm', -1],
          ['rightArm', 1],
        ] as const) {
          const part = obj.avatar.getObjectByName(name);
          if (part) part.rotation.x = walking ? Math.sin(stamp * 0.013) * 0.5 * sign : 0;
        }
        project(x, 2.7, z, obj.label);
      }
      for (const l of locations) project(l.x, 0.7, l.z, labels.current.get(l.id) || null);
      // Dense crowds keep their bodies visible; nearby names yield to map pins and one another.
      const occupied: { x: number; y: number; width: number; height: number }[] = [];
      const screenRect = (x: number, y: number, z: number, width: number, height: number) => {
        projected.set(x, y, z).project(camera);
        return { x: (projected.x * 0.5 + 0.5) * w, y: (-projected.y * 0.5 + 0.5) * h, width, height };
      };
      for (const l of locations)
        occupied.push(
          screenRect(l.x, 0.7, l.z, Math.min(145, l.name[state.current.locale].length * 6 + 35), 26),
        );
      occupied.push(screenRect(avatar.position.x, 2.8, avatar.position.z, 100, 24));
      let shownNames = 0;
      for (const obj of [...peerObjects.values()].sort(
        (a, b) =>
          a.avatar.position.distanceToSquared(avatar.position) -
          b.avatar.position.distanceToSquared(avatar.position),
      )) {
        const rect = screenRect(
          obj.avatar.position.x,
          2.7,
          obj.avatar.position.z,
          Math.min(135, (obj.label.textContent?.length || 0) * 4.7 + 16),
          20,
        );
        const onScreen = rect.x > 0 && rect.x < w && rect.y > 0 && rect.y < h;
        const visible =
          onScreen &&
          (!!obj.label.dataset.emote ||
            (shownNames < (w < 700 ? 4 : 8) &&
              !occupied.some(
                (r) =>
                  Math.abs(r.x - rect.x) < (r.width + rect.width) / 2 + 4 &&
                  Math.abs(r.y - rect.y) < (r.height + rect.height) / 2 + 3,
              )));
        obj.label.style.visibility = visible ? 'visible' : 'hidden';
        if (visible) {
          shownNames++;
          occupied.push(rect);
        }
      }
      const place = locations.find((l) => l.id === s);
      if (place) clickRing.position.set(place.x, 0.065, place.z);
      npcAvatars.forEach((a, i) => {
        a.rotation.y = 0.4 + Math.sin(stamp * 0.00035 + i) * 0.15;
        project(a.position.x, 2.05, a.position.z, npcLabels.current.get(npcs[i]!.id) || null);
      });
      crowd.update([avatar, ...npcAvatars, ...Array.from(peerObjects.values(), (p) => p.avatar)]);
      for (const item of boards)
        item.board.update(state.current.billboards[item.placement], state.current.locale);
      renderer.render(scene, camera);
      const measure = stamp - measuredAt >= 200;
      if (measure) measuredAt = stamp;
      const ownLabel = avatarLabel.current;
      const ownVisible =
        !!state.current.character && !!ownLabel && !ownLabel.hidden && ownLabel.style.visibility !== 'hidden';
      let ownRect: DOMRect | undefined;
      let labelViewport: DOMRect | undefined;
      if (measure && ownVisible) {
        const style = getComputedStyle(ownLabel);
        if (style.display !== 'none' && style.visibility === 'visible' && Number(style.opacity) > 0) {
          const rect = ownLabel.getBoundingClientRect();
          if (
            rect.width > 0 &&
            rect.height > 0 &&
            rect.bottom > 0 &&
            rect.top < innerHeight &&
            rect.right > 0 &&
            rect.left < innerWidth
          ) {
            ownRect = rect;
            labelViewport = el.getBoundingClientRect();
          }
        }
      }
      for (const item of boards) {
        const delivery = item.board.delivery;
        const valid = !!delivery && performance.now() < delivery.expiresAt;
        item.label.hidden = !valid || !item.board.ready;
        if (valid && item.board.ready) {
          const point = item.board.structure.labelPoint
            .clone()
            .applyMatrix4(item.board.structure.group.matrixWorld);
          item.label.textContent = `${sponsorLabel[state.current.locale]} · ${delivery.campaign.sponsor}`;
          project(point.x, point.y, point.z, item.label);
          if (measure) {
            item.labelShiftX = 0;
            item.labelShiftY = 0;
            if (ownRect && labelViewport && item.label.style.visibility !== 'hidden') {
              const rect = item.label.getBoundingClientRect();
              if (
                rect.left < ownRect.right + 4 &&
                rect.right > ownRect.left - 4 &&
                rect.top < ownRect.bottom + 4 &&
                rect.bottom > ownRect.top - 4
              ) {
                const shift = Math.max(20, rect.bottom - ownRect.top + 4);
                const left = Math.max(0, labelViewport.left) + 4;
                const right = Math.min(innerWidth, labelViewport.right) - 4;
                const candidateLeft = Math.min(Math.max(rect.left, left), right - rect.width);
                if (
                  rect.width <= right - left &&
                  rect.top - shift >= Math.max(0, labelViewport.top) + 4 &&
                  rect.bottom - shift <= Math.min(innerHeight, labelViewport.bottom) - 4
                ) {
                  item.labelShiftX = candidateLeft - rect.left;
                  item.labelShiftY = shift;
                }
              }
            }
          }
          if (!ownVisible) {
            item.labelShiftX = 0;
            item.labelShiftY = 0;
          }
          if (item.labelShiftX || item.labelShiftY)
            item.label.style.transform += ` translate(${item.labelShiftX}px,-${item.labelShiftY}px)`;
        } else {
          item.labelShiftX = 0;
          item.labelShiftY = 0;
        }
        if (delivery && measure) {
          const rect = renderer.domElement.getBoundingClientRect();
          const fraction =
            valid && item.board.ready && elementIsPainted(renderer.domElement)
              ? billboardVisibility(
                  item.board.structure.face,
                  camera,
                  [
                    city,
                    crowd.group,
                    ...boards.map((b) => b.board.structure.group),
                    ...(garden ? [garden] : []),
                  ],
                  {
                    left: rect.left,
                    top: rect.top,
                    width: rect.width,
                    height: rect.height,
                    screenWidth: innerWidth,
                    screenHeight: innerHeight,
                  },
                  (x, y) => document.elementFromPoint(x, y) === renderer.domElement,
                  adRay,
                )
              : 0;
          delivery.measurement.observe(
            {
              painted:
                item.board.ready && item.paintedToken === delivery.campaign.deliveryToken && fraction > 0,
              visibleFraction: fraction,
              foreground: document.visibilityState === 'visible',
            },
            performance.now(),
          );
        }
        item.paintedToken = valid && item.board.ready ? delivery.campaign.deliveryToken : '';
      }
      if (import.meta.env.DEV && stamp - metricsAt >= 1000) {
        el.dataset.sceneMetrics = JSON.stringify({
          drawCalls: renderer.info.render.calls,
          triangles: renderer.info.render.triangles,
          geometries: renderer.info.memory.geometries,
          peers: state.current.peers.length,
          framesPerSecond: Math.round((metricsFrames * 10000) / (stamp - metricsAt)) / 10,
          viewport: [w, h],
        });
        metricsAt = stamp;
        metricsFrames = 0;
      }
      metricsFrames++;
    };
    raf = requestAnimationFrame(draw);
    const ray = new THREE.Raycaster(),
      plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    const hit = new THREE.Vector3();
    const pointer = new THREE.Vector2();
    const click = (e: PointerEvent) => {
      if (e.target !== renderer.domElement || !state.current.character) return;
      const r = el.getBoundingClientRect();
      pointer.set(((e.clientX - r.left) / r.width) * 2 - 1, (-(e.clientY - r.top) / r.height) * 2 + 1);
      ray.setFromCamera(pointer, camera);
      const target = ray.intersectObjects(
        [city, crowd.group, ...boards.map((b) => b.board.structure.group), ...(garden ? [garden] : [])],
        true,
      )[0];
      const advertisement = boards.find((item) => target?.object === item.board.structure.face);
      if (advertisement) {
        const delivery = advertisement.board.delivery;
        if (
          delivery &&
          advertisement.board.ready &&
          performance.now() < delivery.expiresAt &&
          safeClickPath(delivery.campaign.clickPath)
        )
          window.open(delivery.campaign.clickPath, '_blank', 'noopener,noreferrer');
        return;
      }
      if (ray.ray.intersectPlane(plane, hit)) {
        const l = [...locations].sort(
          (a, b) => Math.hypot(a.x - hit.x, a.z - hit.z) - Math.hypot(b.x - hit.x, b.z - hit.z),
        )[0];
        if (l && Math.hypot(l.x - hit.x, l.z - hit.z) < 6) state.current.onPlace(l.id);
      }
    };
    el.addEventListener('pointerup', click);
    return () => {
      cancelAnimationFrame(raf);
      for (const obj of peerObjects.values()) obj.label.remove();
      observer.disconnect();
      el.removeEventListener('pointerup', click);
      crowd.dispose();
      for (const item of boards) {
        item.board.delivery?.measurement.pause();
        item.board.dispose();
        item.label.remove();
      }
      renderer.dispose();
      renderer.domElement.remove();
      scene.traverse((o) => {
        if (o instanceof THREE.InstancedMesh) o.dispose();
      });
    };
  }, []);
  return (
    <div className="city-scene" ref={host} aria-label="New York 3D neighborhood">
      {failed && (
        <div className="scene-fallback">
          {
            text(
              '3D unavailable. Use the neighborhood menu to play.',
              '3D 暂不可用，可使用街区菜单继续游戏。',
              '3D 暫不可用，可使用街區選單繼續遊戲。',
              '3D no disponible. Puedes jugar desde el menú del barrio.',
            )[locale]
          }
        </div>
      )}
      {npcs.map((n) => (
        <div
          className="npc-scene-label"
          key={n.id}
          title={`${n.name} · ${n.role[locale]} · NPC`}
          ref={(node) => {
            if (node) npcLabels.current.set(n.id, node);
          }}
        >
          NPC
        </div>
      ))}
      {locations.map((l) => {
        const Icon = icons[l.id] || MapPin;
        return (
          <button
            className={`place-pin ${selected === l.id ? 'selected' : ''} ${character?.data.location === l.id ? 'current' : ''}`}
            key={l.id}
            ref={(n) => {
              if (n) labels.current.set(l.id, n);
            }}
            onClick={() => onPlace(l.id)}
          >
            <Icon size={14} />
            <span>{l.name[locale]}</span>
          </button>
        );
      })}
      <div ref={avatarLabel} className="avatar-label" hidden={!character}>
        <span />
        {character?.name}
        <b ref={ownEmoteLabel} hidden style={{ fontSize: 20, lineHeight: 1 }} />
      </div>
      <div className="scene-zoom">
        {character &&
          onEmote &&
          EMOTE_PRESETS.map((preset) => (
            <button
              key={preset}
              aria-label={emoteCopy[preset].label[locale]}
              title={emoteCopy[preset].label[locale]}
              disabled={!emotesEnabled || !!activeEmote(emotes[character.id], Date.now() + offset)}
              style={{ justifyContent: 'center', fontSize: 19, lineHeight: 1 }}
              onClick={() => onEmote(preset)}
            >
              {emoteCopy[preset].emoji}
            </button>
          ))}
        <button aria-label={translate(locale, 'zoomIn')} onClick={() => controls.current(0.15)}>
          <Plus size={18} />
        </button>
        <button aria-label={translate(locale, 'zoomOut')} onClick={() => controls.current(-0.15)}>
          <Minus size={18} />
        </button>
      </div>
    </div>
  );
}
